import { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, X, Link as LinkIcon, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import ImageCropModal from './ImageCropModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB — banners can be a bit larger than product photos
const ALLOWED_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'avif', 'heic', 'heif'];

const RATIO_PRESETS = [
  { id: 'wide', label: '3:1 (Homepage Hero)', aspect: 3 / 1, dims: '1500×500 px' },
  { id: '16:9', label: '16:9 (Standard)', aspect: 16 / 9, dims: '1600×900 px' },
  { id: '21:9', label: '21:9 (Ultra Wide)', aspect: 21 / 9, dims: '2100×900 px' },
];

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const BannerImageUploader = ({ value, onChange, disabled }) => {
  const [mode, setMode] = useState('upload'); // 'upload' | 'url'
  const [ratio, setRatio] = useState(RATIO_PRESETS[0]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [cropSrc, setCropSrc] = useState(null); // { src, fileName }
  const fileInputRef = useRef(null);

  const openFileDialog = () => {
    if (disabled || uploading) return;
    setError('');
    fileInputRef.current?.click();
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXTS.includes(ext)) {
      setError('Unsupported file type. Use JPG, PNG, WebP, AVIF, or HEIC.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('File too large. Maximum size is 8 MB.');
      return;
    }

    try {
      const src = await fileToDataUrl(file);
      setCropSrc({ src, fileName: file.name });
    } catch (err) {
      setError('Could not read file.');
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('admin_token');
    // skip_backdrop=true: banners are hero photos - do NOT whiten uniform backdrops
    const res = await axios.post(`${API}/upload-image?skip_backdrop=true`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    });
    return `${BACKEND_URL}${res.data.url}`;
  };

  const finalizeAndUpload = async (file) => {
    setUploading(true);
    setError('');
    try {
      const url = await uploadFile(file);
      onChange(url);
      toast.success('Banner image uploaded');
    } catch (err) {
      console.error('Banner upload failed:', err);
      const msg = err?.response?.data?.detail || 'Upload failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleCropSave = async (croppedFile) => {
    setCropSrc(null);
    await finalizeAndUpload(croppedFile);
  };

  const handleCropSkip = async () => {
    // Use the original data URL as the file (skip cropping)
    if (!cropSrc) return;
    try {
      const res = await fetch(cropSrc.src);
      const blob = await res.blob();
      const file = new File([blob], cropSrc.fileName || 'banner.jpg', {
        type: blob.type || 'image/jpeg',
      });
      setCropSrc(null);
      await finalizeAndUpload(file);
    } catch (err) {
      setCropSrc(null);
      setError('Failed to process image.');
    }
  };

  const handleCropCancel = () => setCropSrc(null);

  const handleClearImage = () => {
    onChange('');
    setError('');
  };

  return (
    <div className="space-y-3" data-testid="banner-image-uploader">
      <div className="flex items-center justify-between">
        <Label>Banner Image</Label>
        <div className="flex items-center gap-1 text-xs">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`px-2 py-1 rounded ${mode === 'upload' ? 'bg-[#0047AB] text-white' : 'text-slate-500 hover:text-slate-800'}`}
            data-testid="banner-uploader-mode-upload"
          >
            <Upload className="w-3 h-3 inline mr-1" /> Upload
          </button>
          <button
            type="button"
            onClick={() => setMode('url')}
            className={`px-2 py-1 rounded ${mode === 'url' ? 'bg-[#0047AB] text-white' : 'text-slate-500 hover:text-slate-800'}`}
            data-testid="banner-uploader-mode-url"
          >
            <LinkIcon className="w-3 h-3 inline mr-1" /> URL
          </button>
        </div>
      </div>

      {/* Current preview */}
      {value ? (
        <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
          <img
            src={value}
            alt="Banner preview"
            className="w-full max-h-56 object-cover"
            data-testid="banner-image-preview"
          />
          <button
            type="button"
            onClick={handleClearImage}
            disabled={disabled || uploading}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 hover:bg-white text-red-600 flex items-center justify-center shadow"
            title="Remove image"
            data-testid="banner-image-remove"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 h-32 flex items-center justify-center text-slate-400 text-sm">
          <ImageIcon className="w-6 h-6 mr-2" /> No image selected
        </div>
      )}

      {mode === 'upload' ? (
        <>
          {/* Aspect ratio picker */}
          <div>
            <Label className="text-xs text-slate-500">Recommended crop</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {RATIO_PRESETS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRatio(r)}
                  className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                    ratio.id === r.id
                      ? 'bg-[#0047AB] border-[#0047AB] text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`banner-ratio-${r.id}`}
                >
                  {r.label}
                  <span className="block text-[10px] opacity-80">{r.dims}</span>
                </button>
              ))}
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={openFileDialog}
            disabled={disabled || uploading}
            className="w-full border-dashed"
            data-testid="banner-upload-btn"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-[#0047AB] border-t-transparent rounded-full animate-spin mr-2" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                {value ? 'Change banner image' : 'Choose image to upload'}
              </>
            )}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/avif,image/heic,image/heif"
            onChange={handleFileChosen}
            className="hidden"
            data-testid="banner-file-input"
          />
          <p className="text-xs text-slate-400">
            JPG, PNG, WebP, AVIF, HEIC · up to 8 MB. You can crop, zoom &amp; reposition in the next step.
          </p>
        </>
      ) : (
        <div className="space-y-1">
          <Input
            type="url"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://example.com/banner.jpg"
            disabled={disabled}
            data-testid="banner-image-url-input"
          />
          <p className="text-xs text-slate-400">Paste a direct image URL (must end with .jpg/.png/.webp).</p>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 border border-red-200 rounded px-2 py-1.5">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <ImageCropModal
        open={Boolean(cropSrc)}
        imageSrc={cropSrc?.src}
        fileName={cropSrc?.fileName}
        aspect={ratio.aspect}
        recommendedDimensions={ratio.dims}
        onCropComplete={handleCropSave}
        onSkip={handleCropSkip}
        onCancel={handleCropCancel}
      />
    </div>
  );
};

export default BannerImageUploader;
